import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { runtimeStatusSchema, runtimeTurnResultSchema, type AgentConfigurationSummary, type RuntimeTurnResult } from '@eairlines/contracts';
import { request } from '../api/client';
import { assertCurrentSession, getSession } from '../auth/session';

export function RuntimePanel({ summary }: { summary: AgentConfigurationSummary }) {
  const client = useQueryClient();
  const origin = getSession();
  const published = summary.versions.find((item) => item.version === summary.publishedVersion);
  const [message, setMessage] = useState('What can you help with?');
  const [sessionId, setSessionId] = useState<string>();
  const [turn, setTurn] = useState<RuntimeTurnResult>();
  const readOnly = import.meta.env.VITE_READ_ONLY_PREVIEW === 'true';
  const path = `/agents/${encodeURIComponent(summary.agentId)}/configuration/runtime`;
  const activation = useMutation({
    mutationKey: ['agent-configuration', summary.agentId, 'activate'],
    mutationFn: async () => {
      assertCurrentSession(origin);
      if (readOnly || !published) throw new Error('A published configuration and write access are required.');
      const result = runtimeStatusSchema.parse(await request(`${path}/activate`, { method: 'POST', body: JSON.stringify({ version: published.version, contentHash: published.contentHash }) }));
      assertCurrentSession(origin);
      return result;
    },
    onSuccess: () => { assertCurrentSession(origin); void client.invalidateQueries({ queryKey: ['agent-configuration', summary.agentId] }); },
  });
  const conversation = useMutation({
    mutationKey: ['agent-configuration', summary.agentId, 'turn'],
    mutationFn: async () => {
      assertCurrentSession(origin);
      if (readOnly || !published || summary.deliveryStatus !== 'ACTIVE') throw new Error('Activate the current publication before sending a turn.');
      const channel = published.payload.scope.channel.toLowerCase();
      const result = runtimeTurnResultSchema.parse(await request(`${path}/turn`, { method: 'POST', body: JSON.stringify({
        message, channel: channel === 'chat' ? 'web' : channel === 'voice' ? 'ivr' : channel,
        market: published.payload.scope.market, language: published.payload.scope.language, sessionId,
      }) }));
      assertCurrentSession(origin);
      return result;
    },
    onSuccess: (result) => { assertCurrentSession(origin); setSessionId(result.sessionId); setTurn(result); },
  });
  return <section className="panel configuration-section" aria-label="Project runtime">
    <h2>Project-local agent runtime</h2>
    <p>Runs deterministic FAQ, booking-enquiry and servicing guidance in this project. It does not call airline systems, retrieve passenger records or execute purchases.</p>
    <p role="status">Runtime: {summary.deliveryStatus} · {summary.runtimeVerified ? 'activation confirmed' : 'activation not confirmed'}</p>
    {summary.activation ? <p>Version {summary.activation.version} · {summary.activation.environment} · activated {summary.activation.activatedAt}<br />Hash: <code>{summary.activation.contentHash}</code></p> : null}
    <p>Publishing or rolling back requires activation of the exact published version. Unsupported policy controls fail closed. Offer windows are checked on every turn.</p>
    <button className="button" disabled={readOnly || !published || activation.isPending || conversation.isPending} onClick={() => activation.mutate()}>Activate published version</button>
    {activation.isError ? <p role="alert">Activation was not confirmed: {activation.error.message}</p> : null}
    <form onSubmit={(event) => { event.preventDefault(); conversation.mutate(); }}>
      <label>Message to the active agent<textarea value={message} maxLength={4000} onChange={(event) => setMessage(event.target.value)} disabled={readOnly || conversation.isPending} /></label>
      <button className="button" disabled={readOnly || summary.deliveryStatus !== 'ACTIVE' || !message.trim() || conversation.isPending || activation.isPending}>Send to agent</button>
      <button className="button" type="button" disabled={readOnly || conversation.isPending} onClick={() => { setSessionId(undefined); setTurn(undefined); }}>New conversation</button>
    </form>
    {conversation.isError ? <p role="alert">Turn failed: {conversation.error.message}</p> : null}
    {turn ? <article aria-label="Agent response"><h3>{turn.agentType} · {turn.decision} · v{turn.version}</h3><p>{turn.response || 'Output withheld by policy.'}</p><p>Applied messages: {turn.appliedMessageIds.join(', ') || 'None'}</p><p>Policy decisions: {turn.policyReasons.join(', ') || 'None'}</p></article> : null}
  </section>;
}
